import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import ZazuLogo from '@/components/ZazuLogo';
import { CheckCircle, XCircle, Mail, Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error, no-token
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      setMessage('No verification token provided.');
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await axios.post(`${API_URL}/api/auth/verify-email?token=${token}`);
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Verification failed. The link may be invalid or expired.');
      }
    };

    verifyEmail();
  }, [token]);

  const handleResendVerification = async () => {
    if (!resendEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification?email=${encodeURIComponent(resendEmail)}`);
      toast.success('If the email exists, a verification link has been sent.');
      setResendEmail('');
    } catch (error) {
      toast.error('Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-teal-50 flex flex-col items-center justify-center p-4">
      <Link to="/" className="mb-8">
        <ZazuLogo size="lg" />
      </Link>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="p-8">
            {status === 'loading' && (
              <div className="text-center py-8">
                <Loader2 className="w-16 h-16 text-emerald-500 animate-spin mx-auto mb-4" />
                <h2 className="text-xl font-bold text-slate-800 mb-2">Verifying your email...</h2>
                <p className="text-slate-500">Please wait a moment.</p>
              </div>
            )}
            
            {status === 'success' && (
              <div className="text-center py-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-12 h-12 text-emerald-500" />
                  </div>
                </motion.div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Email Verified!</h2>
                <p className="text-slate-500 mb-6">{message}</p>
                <Button 
                  onClick={() => navigate('/login')}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="go-to-login"
                >
                  Go to Login <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
            
            {status === 'error' && (
              <div className="text-center py-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <XCircle className="w-12 h-12 text-red-500" />
                  </div>
                </motion.div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Verification Failed</h2>
                <p className="text-slate-500 mb-6">{message}</p>
                
                <div className="space-y-4 text-left border-t pt-6 mt-6">
                  <p className="text-sm text-slate-600 text-center">Need a new verification link?</p>
                  <div>
                    <Label htmlFor="resend-email">Email Address</Label>
                    <Input
                      id="resend-email"
                      type="email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="mt-1"
                      data-testid="resend-email-input"
                    />
                  </div>
                  <Button 
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="w-full"
                    variant="outline"
                    data-testid="resend-verification-btn"
                  >
                    {resending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    Resend Verification Email
                  </Button>
                </div>
              </div>
            )}
            
            {status === 'no-token' && (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-12 h-12 text-amber-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Verify Your Email</h2>
                <p className="text-slate-500 mb-6">
                  Please check your inbox for the verification link, or request a new one below.
                </p>
                
                <div className="space-y-4 text-left">
                  <div>
                    <Label htmlFor="resend-email">Email Address</Label>
                    <Input
                      id="resend-email"
                      type="email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="mt-1"
                      data-testid="resend-email-input"
                    />
                  </div>
                  <Button 
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="w-full bg-emerald-500 hover:bg-emerald-600"
                    data-testid="resend-verification-btn"
                  >
                    {resending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    Send Verification Email
                  </Button>
                  
                  <div className="text-center pt-4">
                    <Link to="/login" className="text-sm text-emerald-600 hover:underline">
                      Back to Login
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;
